import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState
} from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import dayjs from 'dayjs';
import { formatCurrencyWithSeparatorWithoutSpace, formatCurrencyWithSpace } from '../../../helpers';
import { SubmitConfirmModal } from '../../../components/ConfirmModal/SubmitConfirmModal';
import { extractPathInfo } from '../../../helpers/extractPathInfo';
import {
  useRecoilValue,
  useRecoilCallback,
  useSetRecoilState
} from 'recoil';
import {
  ArrowDown
} from '../../../assets';
import {
  Accordion,
  ErrorModalSection,
  Footer,
  TableComponent
} from '../../../components';
import { useLoader } from '../../../components/Loader/useLoader';
import { useModal } from '../../../components/modalController/useModal';
import {
  AccordionStyle,
  Colors,
  Fonts,
  appTermText
} from '../../../constants';
import {
  offerDetailsByOfferId,
  useIsMobile,
  useIsSuperMobile,
  userEmail,
  offersListState,
  businessList,
  acceptOffers,
  applicationListRefreshTrigger
} from '../../../recoil';
import { useTealium } from '../../../tealium/useTealium';
import { Button } from '../../../components/v3/Button/Button';

const styles = {
  container: {
    margin: '16px 8px',
    paddingRight: '20px'
  },
  titleContainer: {
    margin: '40px 0 16px'
  },
  titleHeading: {
    fontFamily: Fonts.RobotoMedium,
    fontSize: '30px',
    color: Colors.GreyScale12,
    padding: '16px 0 8px'
  },

  listStyle: {
    listStyle: 'auto'
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '50px'
  },
  descriptionTitle: {
    display: 'flex',
    justifyContent: 'left',
    fontFamily: Fonts.RobotoBold,
    fontSize: '1em',
    lineHeight: '30px',
    color: Colors.GreyScale12,
    width: 'inherit'
  },
  descriptionContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    fontFamily: Fonts.Roboto,
    fontSize: '1em',
    lineHeight: '25px',
    color: Colors.GreyScale12,
    margin: '5px 0 20px 0',
    paddingLeft: '20px'
  },
  fundingAccordion: {
    display: 'flex',
    width: '100%'
  },
  fundingDetailContainer: {
    ...AccordionStyle.accordionChildren,
    display: 'flex',
    flexDirection: 'column',
    width: '94%'
  },
  fundingDetailsInfo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingBottom: '10px'
  },
  fundingDetailsInfoOdd: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: Colors.GreyScale2,
    paddingBottom: '10px'
  },
  fundingDetailsTitle: {
    display: 'flex',
    fontFamily: Fonts.RobotoMedium,
    fontSize: '1em',
    lineHeight: '15px',
    color: Colors.GreyScale12,
    margin: '10px'
  },
  fundingDetailsValue: {
    display: 'flex',
    fontFamily: Fonts.RobotoMedium,
    fontSize: '1em',
    lineHeight: '15px',
    color: Colors.GreyScale12,
    margin: '10px'
  },
  terms: {
    color: Colors.GreyScale10,
    fontSize: 14,
    fontFamily: Fonts.Roboto,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 100,
    width: '95%'
  },
  termsLink: {
    color: Colors.Primary4,
    fontSize: 14,
    fontFamily: Fonts.Roboto,
    cursor: 'pointer',
    marginLeft: 2
  },
  accordionStyle: {
    cursor: 'pointer',
    borderRadius: 16,
    border: `1px solid ${Colors.GreyScale5}`,
    paddingTop: 20,
    paddingBottom: 20,
    marginBottom: 30
  },
  RepaymentInfoList: {
    fontFamily: Fonts.Roboto,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0px'
  },
  accordionTitle: {
    fontFamily: Fonts.RobotoMedium,
    fontSize: '18px',
    lineHeight: '24px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontWeight: 500
  },
  accordionWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  viewQuoteBtnStyle: {
    borderRadius: '50px',
    backgroundColor: '#007C92',
    display: 'inline-block',
    width: 'auto',
    border: 'none',
    padding: '5px 15px'
  }
};

const mobileStyles = {
  container: {
    margin: '16px'
  },
  titleContainer: {
    margin: '16px 0 8px'
  },
  titleHeading: {
    ...styles.titleHeading,
    fontSize: '24px',
    textAlign: 'center'
  },
  descriptionContainer: {
    ...styles.descriptionContainer,
    marginTop: '10px'
  },
  descriptionContent: {
    ...styles.descriptionContent,
    padding: '0px 16px 0 24px',
    marginTop: '16px'
  },
  viewQuoteBtnStyle: {
    borderRadius: '50px',
    backgroundColor: '#007C92',
    display: 'inline-block',
    width: 'auto',
    border: 'none',
    minWidth: '100px',
    padding: '5px 10px',
    fontSize: '13px'
  }
};

const OfferAccordion = ({ offer, businessName }) => {
  const isMobile = useRecoilValue(useIsMobile);
  const isSuperMobile = useRecoilValue(useIsSuperMobile);
  const accordionStyleFn = (data) => ({ ...(isMobile ? { ...data, borderRadius: 20 } : data) });

  return (
    <Accordion
      isOpen={false}
      headerStyle={{ width: isMobile ? '90%' : '100%' }}
      headerChildren={
        <>
          <div style={styles.accordionWrap}>
            <div style={AccordionStyle.accordionHeaderLeft}>
              <div style={styles.accordionTitle}>
                {businessName}: {offer?.productName}
              </div>
            </div>
            <div style={styles.arrowContainer}>
              <Button type='button'
                style={isSuperMobile ? mobileStyles.viewQuoteBtnStyle : styles.viewQuoteBtnStyle}
                variant='secondary'
              >
                View Quote
              </Button>
            </div>
          </div>
        </>
      }
      containerStyle={accordionStyleFn(styles.accordionStyle)}
    >
      {''}
    </Accordion>
  );
};

OfferAccordion.propTypes = {
  offer: PropTypes.object,
  businessName: PropTypes.string
};

const QuoteAccordion = ({ businessName, selectedOfferDetail, isOpen, toggleOpen }) => {

  const isMobile = useRecoilValue(useIsMobile);
  const accordionStyleFn = (data) => ({ ...(isMobile ? { ...data, borderRadius: 20 } : data) });
  const fundingDetailsInfoOdd = isMobile ? { ...styles.fundingDetailsInfoOdd, flexDirection: 'column' } : styles.fundingDetailsInfoOdd;
  const fundingDetailsInfo = isMobile ? { ...styles.fundingDetailsInfo, flexDirection: 'column' } : styles.fundingDetailsInfo;
  const termText = appTermText[selectedOfferDetail?.tenure];
  const accordionTitleStyle = isMobile ? { ...AccordionStyle.accordionTitle, fontSize: '17.28px' } : AccordionStyle.accordionTitle;

  return (
    <Accordion
      isOpen={isOpen}
      headerStyle={{ width: isMobile ? '90%' : '100%' }}
      headerChildren={
        <>
          <div style={AccordionStyle.accordionHeaderLeft}>
            <div style={accordionTitleStyle}>
              Funding details
            </div>
          </div>

          <div>
            <img
              style={{ ...AccordionStyle.ArrowImg, rotate: isOpen ? '180deg' : '' }}
              src={ArrowDown}
              alt='DropdownOpen'
              role='presentation'
              onClick={() => toggleOpen()}
              onKeyDown={() => toggleOpen()}
            />
          </div>
        </>
      }
      containerStyle={accordionStyleFn(styles.accordionStyle)}
    >
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={isMobile ? { ...styles.fundingDetailContainer, width: '90%' } : styles.fundingDetailContainer}>
          {selectedOfferDetail && (
            <>
              <div style={fundingDetailsInfoOdd}>
                <div style={styles.fundingDetailsTitle}>Advance amount</div>
                <div style={styles.fundingDetailsValue}>{formatCurrencyWithSpace(selectedOfferDetail.advanceAmount)}</div>
              </div>
              <div style={fundingDetailsInfo}>
                <div style={styles.fundingDetailsTitle}>Company name</div>
                <div style={styles.fundingDetailsValue}>{businessName}</div>
              </div>
              <div style={fundingDetailsInfoOdd}>
                <div style={styles.fundingDetailsTitle}>No of months</div>
                <div style={styles.fundingDetailsValue}>{termText}</div>
              </div>
              <div style={fundingDetailsInfo}>
                <div style={styles.fundingDetailsTitle}>Total repayment</div>
                <div style={styles.fundingDetailsValue}>{formatCurrencyWithSpace(selectedOfferDetail.totalRepayment)}</div>
              </div>
            </>
          )}
        </div>

      </div>
    </Accordion>
  );
};

QuoteAccordion.propTypes = {
  businessName: PropTypes.string,
  selectedOfferDetail: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired
};

const RepaymentHeader = {
  date: 'Date',
  capital: 'Capital',
  cost: 'Cost',
  repayment: 'Repayment amount'
};

const RepaymentAccordion = ({ selectedOfferDetail, isOpen, toggleOpen }) => {
  const isMobile = useRecoilValue(useIsMobile);
  const accordionStyleFn = (data) => ({ ...(isMobile ? { ...data, borderRadius: 20 } : data) });
  const accordionTitleStyle = isMobile ? { ...AccordionStyle.accordionTitle, fontSize: '17.28px' } : AccordionStyle.accordionTitle;

  return (
    <Accordion
      isOpen={isOpen}
      headerStyle={{ width: isMobile ? '90%' : '100%' }}
      headerChildren={
        <>
          <div style={AccordionStyle.accordionHeaderLeft}>
            <div style={accordionTitleStyle}>
              Expected repayment schedule
            </div>
          </div>

          <div>
            <img
              style={{ ...AccordionStyle.ArrowImg, rotate: isOpen ? '180deg' : '' }}
              src={ArrowDown}
              alt='DropdownOpen'
              role='presentation'
              onClick={toggleOpen}
              onKeyDown={toggleOpen}
            />
          </div>
        </>
      }
      containerStyle={accordionStyleFn(styles.accordionStyle)}
    >
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={isMobile ? { ...styles.fundingDetailContainer, width: '90%' } : styles.fundingDetailContainer}>
          <ul style={{ listStyleType: 'disc', padding: '10px' }}>
            <li style={styles.RepaymentInfoList}>Repayment of your funding is made {selectedOfferDetail?.paymentFrequency} via debit order {selectedOfferDetail?.tenure} (max {selectedOfferDetail?.repaymentSchedule?.length} payments in total)</li>
            <li style={styles.RepaymentInfoList}>If you choose to settle your advance early you will save on all future costs - use the slider to get an indication of potential savings.</li>
            <li style={styles.RepaymentInfoList}>Please note that we do not charge any processing, initiating or monthly service fees. We only charge the cost as per below, simple!</li>
          </ul>
          {selectedOfferDetail?.repaymentSchedule && (
            <TableComponent
              data={
                selectedOfferDetail?.repaymentSchedule.map(d => ({
                  ...d,
                  date: dayjs(d.date).format('DD MMM YYYY'),
                  capital: formatCurrencyWithSeparatorWithoutSpace(d.principalAmount),
                  cost: formatCurrencyWithSeparatorWithoutSpace(d.costAmount),
                  repayment: formatCurrencyWithSeparatorWithoutSpace(d.repaymentAmount)
                }))
              }
              heading={RepaymentHeader}
            />
          )}
        </div>
      </div>
    </Accordion>
  );
};

RepaymentAccordion.propTypes = {
  selectedOfferDetail: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired
};

export const BTAFundingQuote = () => {

  const [openSection, setOpenSection] = useState('funding');
  const email = useRecoilValue(userEmail);
  const navigate = useNavigate();
  const businessListDetails = useRecoilValue(businessList);
  const [selectedOfferDetail, setSelectedOfferDetail] = useState(null);
  const [isOpenConfirm, setOpenConfirm] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [offerId, setOfferId] = useState('');
  const location = useLocation();
  const offerDetail = useRecoilValue(offersListState(extractPathInfo(location?.pathname).businessId || ''));
  const [openModal, closeModal] = useModal();
  const [openLoader, closeLoader] = useLoader();
  const { track, events } = useTealium();
  const refreshApplications = useSetRecoilState(applicationListRefreshTrigger(businessId));
  const isMobile = useRecoilValue(useIsMobile);

  useEffect(() => {
    setSelectedOfferDetail(null);
    track(events.fundingQuote.pageView());
  }, [location?.pathname]);

  const backClick = () => {
    setSelectedOfferDetail(null);
    track(events.fundingQuote.buttonClick('back'));
  };

  const handleSubmitQuote = async () => {
    track(events.fundingQuote.acceptQuote());
    try {
      openLoader();
      await acceptOfferCallback(businessId, offerId);
      track(events.fundingQuote.journeyEnd());
      refreshApplications(prev => prev + 1);
      setOpenConfirm(true);
    } catch (error) {
      track(events.error('event', location.pathname, 'There was an error while submitting quote.', 'bta funding quote'));
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={error?.response?.headers?.['x-amzn-trace-id'] || ''}
          error={error}
          onCloseErrorModal={closeModal}
        />,
        type: 'error'
      });
    } finally {
      closeLoader();
    }
  };

  useEffect(() => {
    let { businessId } = extractPathInfo(location?.pathname);
    businessListDetails?.businesses?.map((business => {
      if (business.businessId === businessId) {
        setBusinessName(business.businessName);
        setBusinessId(businessId);
      }
    }));
  }, [location.pathname]);

  const navigateTo = (path) =>
    navigate(path, {
      state: {
        returnPath: location.pathname
      }
    });

  const goToNextPage = () => {
    track(events.fundingQuote.buttonClick('got it'));
    openLoader();
    setTimeout(() => {
      closeLoader();
      navigateTo(`/applications/${businessId}`);
    }, 100);
  };

  const fetchedOfferDetailCallback = useRecoilCallback(({ snapshot }) => async (businessId, offerId) => {
    openLoader();
    let data = await snapshot.getPromise(offerDetailsByOfferId({ businessId, offerId }));
    closeLoader();
    if (data?.error) {
      navigateTo('/dashboard');
    }
    return data;
  });

  const acceptOfferCallback = useRecoilCallback(({ snapshot }) => async (businessId, offerId) => {
    openLoader();
    let data = await snapshot.getPromise(acceptOffers({ businessId, offerId }));
    closeLoader();
    if (data?.error) {
      navigateTo('/dashboard');
    }
    return data;
  });

  const fetchOfferDetail = async (businessId, offerId) => {
    const offerDetailsResponse = await fetchedOfferDetailCallback(businessId, offerId);
    setSelectedOfferDetail(offerDetailsResponse);
  };

  const handleOfferClick = async (businessId, offerId) => {
    track(events.fundingQuote.fundingAccordion('funding quote', 'expanded'));
    setOfferId(offerId);
    await fetchOfferDetail(businessId, offerId);
  };

  const repaymentSection = () => {
    track(events.fundingQuote.fundingAccordion('expected repayment schedule', openSection === 'repayment' ? 'collapsed' : 'expanded'));
    setOpenSection(openSection === 'repayment' ? '' : 'repayment');
  };

  const fundingSection = () => {
    track(events.fundingQuote.fundingAccordion('funding details', openSection === 'funding' ? 'collapsed' : 'expanded'));
    setOpenSection(openSection === 'funding' ? '' : 'funding');
  };

  const subHeadingText = () => {
    return (
      <>
        <span>
          <p>We have received your response.</p>
          <p>An email with your contract will be sent to:</p>
          <p>{email}</p>
        </span>
      </>
    );
  };

  return (
    <div style={isMobile ? mobileStyles.container : styles.container}>
      <div style={isMobile ? mobileStyles.titleContainer : styles.titleContainer}>
        <h4 style={isMobile ? mobileStyles.titleHeading : styles.titleHeading}>
          Funding quote
        </h4>
      </div>

      {!selectedOfferDetail && offerDetail && offerDetail.offers?.map((offer, index) => (
        <div key={`offer_${index}`} style={styles.fundingAccordion} onClick={() => handleOfferClick(businessId, offer.offerId)}>
          <OfferAccordion offer={offer} businessName={businessName} />
        </div>
      ))}

      {selectedOfferDetail &&
        <div>
          <div style={isMobile ? mobileStyles.descriptionContainer : styles.descriptionContainer}>
            <div style={styles.descriptionTitle}>How it works</div>
            <ol style={isMobile ? mobileStyles.descriptionContent : styles.descriptionContent}>
              <li style={styles.listStyle}>Please review your funding application below.</li>
              <li style={styles.listStyle}>If you are satisfied, please accept the quote.</li>
              <li style={styles.listStyle}>We will then email you a copy of the Advance Agreement which includes a surety form and needs to be digitally signed by yourself.</li>
              <li style={styles.listStyle}>Funds will be transferred to your bank account as soon as the signed documents have been received.</li>
            </ol>
          </div>

          <div style={styles.fundingAccordion}>
            <QuoteAccordion businessName={businessName} selectedOfferDetail={selectedOfferDetail} isOpen={openSection === 'funding'} toggleOpen={fundingSection} />
          </div>

          <div style={styles.fundingAccordion}>
            <RepaymentAccordion selectedOfferDetail={selectedOfferDetail} isOpen={openSection === 'repayment'} toggleOpen={repaymentSection} />
          </div>
        </div>
      }
      {isOpenConfirm &&
        <SubmitConfirmModal
          subHeading={subHeadingText()}
          onButtonClick={goToNextPage}
          heading='Thank You'
          buttonText='Got it!' />
      }

      {selectedOfferDetail &&
        <Footer
          leftTitle={'Back'}
          rightTitle='Accept Quote'
          leftCallback={backClick}
          rightCallback={handleSubmitQuote}
        />
      }
    </div>
  );
};
